import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute, PublicRoute } from './components/RouteGuards';
import DashboardLayout from './layouts/DashboardLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Fleet from './pages/Fleet';
import FleetDashboard from './pages/FleetDashboard';
import VehicleDetail from './pages/VehicleDetail';
import MaintenanceDashboard from './pages/maintenance/MaintenanceDashboard';
import MaintenanceOrders from './pages/maintenance/MaintenanceOrders';
import MaintenanceOrderDetail from './pages/maintenance/MaintenanceOrderDetail';
import MaintenancePlans from './pages/maintenance/MaintenancePlans';
import MaintenanceCalendar from './pages/maintenance/MaintenanceCalendar';
import Checklists from './pages/maintenance/Checklists';
import Tires from './pages/maintenance/Tires';
import FuelRecords from './pages/maintenance/FuelRecords';
import { Corretivas, HistoricoTecnico, ManutencaoConfig, Indicadores, Relatorios } from './pages/maintenance/Extras';
import * as P5 from './pages/phase5';
import * as P6 from './pages/phase6';
import * as FIN from './pages/financial';
import * as RH from './pages/rh';
import * as P8 from './pages/phase8';
import * as P910 from './pages/phase910';
import * as PT from './pages/portals';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Company from './pages/Company';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Contracts from './pages/Contracts';
import CostCenters from './pages/CostCenters';
import Categories from './pages/Categories';
import Tags from './pages/Tags';
import MastersIndex from './pages/MastersIndex';
import MasterDataPage from './pages/MasterDataPage';
import Suppliers from './pages/Suppliers';
import VehicleModels from './pages/VehicleModels';
import FuelStations from './pages/FuelStations';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/clientes" element={<Clients />} />
                <Route path="/frota" element={<Fleet />} />
                <Route path="/frota/dashboard" element={<FleetDashboard />} />
                <Route path="/frota/:id" element={<VehicleDetail />} />
                <Route path="/manutencao" element={<MaintenanceDashboard />} />
                <Route path="/manutencao/os" element={<MaintenanceOrders />} />
                <Route path="/manutencao/os/:id" element={<MaintenanceOrderDetail />} />
                <Route path="/manutencao/preventivas" element={<MaintenancePlans />} />
                <Route path="/manutencao/agenda" element={<MaintenanceCalendar />} />
                <Route path="/manutencao/checklists" element={<Checklists />} />
                <Route path="/manutencao/pneus" element={<Tires />} />
                <Route path="/manutencao/abastecimentos" element={<FuelRecords />} />
                <Route path="/manutencao/corretivas" element={<Corretivas />} />
                <Route path="/manutencao/historico-tecnico" element={<HistoricoTecnico />} />
                <Route path="/manutencao/indicadores" element={<Indicadores />} />
                <Route path="/manutencao/relatorios" element={<Relatorios />} />
                <Route path="/manutencao/configuracoes" element={<ManutencaoConfig />} />
                {/* Fase 5 - Estoque e Compras */}
                <Route path="/estoque" element={<P5.EstoqueDashboard />} />
                <Route path="/estoque/produtos" element={<P5.Produtos />} />
                <Route path="/estoque/categorias" element={<P5.Categorias />} />
                <Route path="/estoque/movimentacoes" element={<P5.Movimentacoes />} />
                <Route path="/estoque/inventario" element={<P5.Inventario />} />
                <Route path="/estoque/almoxarifado" element={<P5.Almoxarifado />} />
                <Route path="/estoque/fornecedores" element={<P5.Fornecedores />} />
                <Route path="/estoque/solicitacoes" element={<P5.Solicitacoes />} />
                <Route path="/estoque/cotacoes" element={<P5.Cotacoes />} />
                <Route path="/estoque/pedidos" element={<P5.Pedidos />} />
                <Route path="/estoque/recebimentos" element={<P5.Recebimentos />} />
                <Route path="/estoque/notas-fiscais" element={<P5.NotasFiscais />} />
                <Route path="/estoque/transferencias" element={<P5.Transferencias />} />
                <Route path="/estoque/relatorios" element={<P5.Relatorios />} />
                {/* Gestão Financeira Integrada */}
                <Route path="/financeiro" element={<FIN.FinancialDashboard />} />
                <Route path="/financeiro/lancamentos" element={<FIN.TransactionsList />} />
                <Route path="/financeiro/pagar" element={<FIN.Payables />} />
                <Route path="/financeiro/receber" element={<FIN.Receivables />} />
                <Route path="/financeiro/fluxo-caixa" element={<FIN.CashFlow />} />
                <Route path="/financeiro/contratos-rentabilidade" element={<FIN.ContractProfitability />} />
                <Route path="/financeiro/custos-frota" element={<FIN.FleetCosts />} />
                <Route path="/financeiro/bancos" element={<FIN.BankAccounts />} />
                <Route path="/financeiro/orcamentos" element={<FIN.Budgets />} />
                <Route path="/financeiro/relatorios" element={<FIN.FinancialReports />} />
                <Route path="/financeiro/dre" element={<FIN.FinancialReports />} />
                <Route path="/financeiro/indicadores" element={<FIN.FinancialDashboard />} />
                {/* Fase 7 - RH */}
                <Route path="/rh" element={<RH.DashboardRH />} />
                <Route path="/rh/funcionarios" element={<RH.EmployeesList />} />
                <Route path="/rh/funcionarios/novo" element={<RH.EmployeeForm />} />
                <Route path="/rh/funcionarios/:id/editar" element={<RH.EmployeeForm />} />
                <Route path="/rh/funcionarios/:id" element={<RH.EmployeeProfile />} />
                {/* Fase 8 - Operação */}
                <Route path="/operacao" element={<P8.OperacaoDashboard />} />
                <Route path="/operacao/linhas" element={<P8.Linhas />} />
                <Route path="/operacao/rotas" element={<P8.Rotas />} />
                <Route path="/operacao/escalas" element={<P8.Escalas />} />
                <Route path="/operacao/agenda" element={<P8.Agenda />} />
                <Route path="/operacao/viagens" element={<P8.Viagens />} />
                <Route path="/operacao/programacao" element={<P8.Programacao />} />
                <Route path="/operacao/disponibilidade" element={<P8.Disponibilidade />} />
                <Route path="/operacao/contratos" element={<P8.ContratosOp />} />
                <Route path="/operacao/calendario" element={<P8.Calendario />} />
                <Route path="/operacao/mapa" element={<P8.MapaOperacional />} />
                {/* Fase 9 - BI */}
                <Route path="/bi" element={<P910.BIDashboard />} />
                <Route path="/bi/kpis" element={<P910.KPIs />} />
                <Route path="/bi/graficos" element={<P910.BIGraficos />} />
                <Route path="/bi/comparativos" element={<P910.Comparativos />} />
                <Route path="/bi/heatmaps" element={<P910.Heatmaps />} />
                <Route path="/bi/rankings" element={<P910.Rankings />} />
                <Route path="/bi/relatorios" element={<P910.BIRelatorios />} />
                <Route path="/bi/exportacoes" element={<P910.Exportacoes />} />
                {/* Fase 10 - Administração */}
                <Route path="/admin" element={<P910.AdminConfig />} />
                <Route path="/admin/logs" element={<P910.AdminLogs />} />
                <Route path="/admin/auditoria" element={<P910.Auditoria />} />
                <Route path="/admin/seguranca" element={<P910.AdminSeguranca />} />
                <Route path="/admin/integracoes" element={<P910.Integracoes />} />
                <Route path="/admin/api" element={<P910.AdminAPI />} />
                <Route path="/admin/webhooks" element={<P910.Webhooks />} />
                <Route path="/admin/backup" element={<P910.AdminBackup />} />
                <Route path="/admin/notificacoes" element={<P910.AdminNotificacoes />} />
                <Route path="/admin/multiempresa" element={<P910.Multiempresa />} />
                <Route path="/admin/lgpd" element={<P910.LGPD />} />
                {/* Portal do Cliente */}
                <Route path="/portal-cliente" element={<PT.PortalClienteDashboard />} />
                <Route path="/portal-cliente/contratos" element={<PT.PortalContratos />} />
                <Route path="/portal-cliente/financeiro" element={<PT.PortalFinanceiro />} />
                <Route path="/portal-cliente/documentos" element={<PT.PortalDocumentos />} />
                <Route path="/portal-cliente/os" element={<PT.PortalOS />} />
                <Route path="/portal-cliente/solicitacoes" element={<PT.PortalSolicitacoes />} />
                <Route path="/portal-cliente/mensagens" element={<PT.PortalMensagens />} />
                <Route path="/portal-cliente/perfil" element={<PT.PortalPerfil />} />
                {/* Portal do Motorista */}
                <Route path="/portal-motorista" element={<PT.PortalMotoristaDashboard />} />
                <Route path="/portal-motorista/escalas" element={<PT.MotoristaEscalas />} />
                <Route path="/portal-motorista/viagens" element={<PT.MotoristaViagens />} />
                <Route path="/portal-motorista/checklists" element={<PT.MotoristaChecklists />} />
                <Route path="/portal-motorista/abastecimentos" element={<PT.MotoristaAbastecimentos />} />
                <Route path="/portal-motorista/ocorrencias" element={<PT.MotoristaOcorrencias />} />
                <Route path="/portal-motorista/documentos" element={<PT.MotoristaDocumentos />} />
                <Route path="/portal-motorista/agenda" element={<PT.MotoristaAgenda />} />
                <Route path="/portal-motorista/perfil" element={<PT.MotoristaPerfil />} />
                <Route path="/usuarios" element={<Users />} />
                <Route path="/perfis" element={<Roles />} />
                <Route path="/empresa" element={<Company />} />
                <Route path="/configuracoes" element={<Settings />} />
                <Route path="/contratos" element={<Contracts />} />
                <Route path="/centros-custo" element={<CostCenters />} />
                <Route path="/categorias" element={<Categories />} />
                <Route path="/tags" element={<Tags />} />
                <Route path="/cadastros" element={<MastersIndex />} />
                <Route path="/cadastros/fornecedores" element={<Suppliers />} />
                <Route path="/cadastros/modelos-veiculos" element={<VehicleModels />} />
                <Route path="/cadastros/postos-combustivel" element={<FuelStations />} />
                <Route path="/cadastros/:key" element={<MasterDataPage />} />
                <Route path="/perfil" element={<Profile />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
